// src/components/layout/UserLayout.js

import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../Navbar'; // Usamos el Navbar reutilizable
import NotificationBell from '../NotificationBell';
import ChangelogWidget from '../ChangelogWidget';
import SelectorFraccionamientoRapido from '../SelectorFraccionamientoRapido';
import SelectorCasaRapido from '../SelectorCasaRapido';
import {
    FaUsers, FaSwimmingPool, FaCogs, FaShieldAlt, FaFileInvoiceDollar, FaLock, FaBars, FaTimes, FaHome, FaBuilding,
    FaBullhorn, FaCalendarAlt, FaHandsHelping, FaRocketchat, FaFileAlt, FaUserEdit, FaQrcode, FaBoxOpen, FaTruck,
    FaUsers as FaUsersIcon, FaChartBar, FaArrowUp, FaArrowDown, FaReceipt, FaWallet, FaQuestionCircle, FaCog
} from 'react-icons/fa';
import { ResidentPendingProvider } from '../../context/ResidentPendingContext';
import { useAuth } from '../../context/AuthContext';
import { useModulos } from '../../hooks/useModulos';
import { useRestriccionMorosidad } from '../../hooks/useRestriccionMorosidad';
import { useResumenCargosAutomaticos } from '../../hooks/useResumenCargosAutomaticos';
import ModalResumenCargosAutomaticos from '../ModalResumenCargosAutomaticos';
import { db } from '../../firebase';
import { doc, getDoc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import './UserLayout.css'; // Crearemos este archivo para que tenga sus propios estilos
import './SidebarUserMobile.css'; // Nuevos estilos para sidebar móvil (user role)
import NotificacionEmergente from '../NotificacionEmergente';
import DomusAIChatWidget from '../DomusAIChatWidget';
import Swal from 'sweetalert2';


const UserLayout = () => {
    const { userData, fraccionamientoId: activeFraccId } = useAuth();
    const fraccionamientoId = activeFraccId || userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

    // Escuchar alerta de mejoras reactiva global para residentes
    useEffect(() => {
        const configGlobalRef = doc(db, 'plataforma', 'config_global');
        const unsubscribe = onSnapshot(configGlobalRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const esResidente = ['residente', 'habitante', 'inquilino', 'propietario'].includes(userData?.rol);
                const aplicaARol = data.rolesAlerta ? Boolean(data.rolesAlerta.residente) : true;

                if (data.alertaMejorasActiva && data.alertaMejorasVersion && esResidente && aplicaARol) {
                    const versionVista = localStorage.getItem('alerta_version_vista');
                    if (versionVista !== data.alertaMejorasVersion && !window.isAlertaMejorasActive) {
                        window.isAlertaMejorasActive = true;
                        Swal.fire({
                            title: data.alertaMejorasTitulo || '¡Actualización del Sistema!',
                            html: `<div style="text-align: left; font-size: 0.95rem; line-height: 1.5;">${data.alertaMejorasMensaje || 'Se han aplicado nuevas mejoras importantes al sistema. Es necesario recargar tu página para poder ver los cambios.'}</div>`,
                            icon: 'info',
                            confirmButtonText: 'Actualizar y Recargar',
                            confirmButtonColor: '#0284c7',
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            allowEnterKey: true
                        }).then((result) => {
                            if (result.isConfirmed) {
                                localStorage.setItem('alerta_version_vista', data.alertaMejorasVersion);
                                window.isAlertaMejorasActive = false;
                                window.location.reload(true);
                            }
                        });
                    }
                }
            }
        }, (err) => {
            console.warn('UserLayout: error escuchando config_global', err);
        });

        return () => unsubscribe();
    }, [userData]);

    const { modulos, loading } = useModulos(fraccionamientoId);
    const { isSectionRestricted, isTabRestricted, isSubSectionRestricted, isRestricted, userDebt } = useRestriccionMorosidad();
    const { resumenNoVisto, marcarComoVisto } = useResumenCargosAutomaticos();
    const [fraccionamientoConfig, setFraccionamientoConfig] = useState({
        logoUrl: null,
        condominioNombre: null
    });
    const location = useLocation();
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const checkIsMobile = () => window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const [isMobile, setIsMobile] = useState(checkIsMobile);
    const navigate = useNavigate();

    // Cargar configuración del fraccionamiento (logo y nombre)
    useEffect(() => {
        let isMounted = true;
        const loadFraccionamientoConfig = async () => {
            if (!fraccionamientoId) return;

            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (isMounted && fraccDoc.exists()) {
                    const data = fraccDoc.data() || {};
                    const configRecibos = data.configuracionRecibos || {};

                    const logoUrl = configRecibos.logoUrl || data.logoUrl || null;
                    const condominioNombre = configRecibos.condominioNombre || data.condominioNombre || data.nombre || null;

                    setFraccionamientoConfig({
                        logoUrl,
                        condominioNombre
                    });
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error al cargar configuración del fraccionamiento:', error);
                }
            }
        };

        loadFraccionamientoConfig();

        return () => { isMounted = false; };
    }, [fraccionamientoId]);

    // Detectar cambios en el tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            const mobile = checkIsMobile();
            setIsMobile(mobile);
            // En móvil, ocultar sidebar por defecto
            if (mobile) {
                setSidebarVisible(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Llamar una vez al montar

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Ocultar sidebar automáticamente cuando cambia la ruta (solo en móvil)
    useEffect(() => {
        if (isMobile) {
            setSidebarVisible(false);
        }
    }, [location.pathname, isMobile]);

    const getInitials = (name) => {
        if (!name) return '';
        const parts = name.trim().split(/\s+/).filter(p => p.length > 0);
        if (parts.length === 0) return '';
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // Determinar si algún módulo de vigilancia está habilitado
    // Primero verificar que el módulo principal de vigilancia esté activo
    const tieneModulosVigilancia = modulos.vigilancia === true && (
        modulos.validar_qr === true
        || modulos.registrar_paquete === true
        || modulos.generar_qr_invitado === true
        || modulos.visitas_frecuentes === true
    );

    // Mapeo de rutas a sectionKeys para restricciones
    const sectionKeyMap = {
        '/comunidad': 'chat', // Chat está dentro de comunidad
        '/amenidades': 'amenidades',
        '/vigilancia': 'visitas',
    };

    // Los enlaces específicos para el rol 'user'. 
    // Se marcan con 'restricted' si están bloqueados por morosidad
    console.log('🔵 [UserLayout] Evaluando enlaces:', {
        loading,
        modulos,
        amenidades: modulos.amenidades,
        tipo_amenidades: typeof modulos.amenidades,
        es_true: modulos.amenidades === true,
        es_false: modulos.amenidades === false
    });

    // Función para crear submenús (solo en móvil para user y superadmin)
    const createSubmenus = useMemo(() => {
        return (baseLink) => {
            if (!isMobile) return baseLink; // No submenús en desktop

            const role = String(userData?.rol || '').toLowerCase();
            if (role !== 'user' && role !== 'superadmin') return baseLink; // Solo user y superadmin

            // Definir submenús según la sección
            let submenu = [];

            if (baseLink.to === '/comunidad') {
                submenu = [
                    { to: '/comunidad/votaciones-avisos', label: 'Votaciones y Avisos', icon: <FaBullhorn />, tabKey: 'comunidad', subKey: 'votaciones_avisos' },
                    { to: '/comunidad/propuestas', label: 'Propuestas e Incidencias', icon: <FaHandsHelping />, tabKey: 'comunidad', subKey: 'propuestas_incidencias' },
                    { to: '/comunidad/eventos', label: 'Eventos y Citas', icon: <FaCalendarAlt />, tabKey: 'comunidad', subKey: 'eventos_citas' },
                ];
                // Agregar Chat Comunitario solo si está habilitado
                if (modulos.chat_comunitario === true) {
                    submenu.push({ to: '/comunidad/chat', label: 'Chat Comunitario', icon: <FaRocketchat />, tabKey: 'comunidad', subKey: 'chat_comunitario' });
                }
                // Agregar Solicitudes y Permisos sin submenús (navegación directa)
                submenu.push({ to: '/comunidad/documentos', label: 'Solicitudes y Permisos', icon: <FaFileAlt />, tabKey: 'comunidad', subKey: 'solicitudes_permisos' });
                submenu.push(
                    { to: '/comunidad/contacto-proveedores', label: 'Directorio de Contactos', icon: <FaUserEdit />, tabKey: 'comunidad', subKey: 'directorio_contactos' }
                );
            } else if (baseLink.to === '/amenidades') {
                // Amenidades no tiene submenús visibles en las rutas, pero podemos agregar opciones si es necesario
                // Por ahora, no tiene submenús
            } else if (baseLink.to === '/vigilancia') {
                // Vigilancia tiene diferentes funcionalidades pero todas en la misma página
                // Por ahora, no tiene submenús
            } else if (baseLink.to === '/finanzas') {
                // Para perfil user, no mostrar submenú (muy poco contenido)
                if (role === 'user') {
                    // No agregar submenú
                } else {
                    // Para superadmin, mantener submenú
                    submenu = [
                        { to: '/finanzas/ingresos', label: 'Ingresos', icon: <FaArrowUp /> },
                        { to: '/finanzas/egresos', label: 'Egresos', icon: <FaArrowDown /> },
                        { to: '/finanzas/recibos-eliminados', label: 'Recibos Eliminados', icon: <FaReceipt /> },
                    ];
                }
            } else if (baseLink.to === '/configuraciones') {
                // Para perfil user, no mostrar submenú (muy poco contenido)
                if (role === 'user') {
                    // No agregar submenú
                } else {
                    // Para superadmin, mantener submenú
                    submenu = [
                        { to: '/configuraciones/perfil', label: 'Perfil', icon: <FaUserEdit /> },
                        { to: '/configuraciones/faq', label: 'Preguntas Frecuentes', icon: <FaQuestionCircle /> },
                        { to: '/configuraciones/soporte', label: 'Soporte', icon: <FaCog /> },
                        { to: '/configuraciones/recibos', label: 'Recibos', icon: <FaReceipt /> },
                    ];
                }
            }

            // Aplicar restricciones a los submenús
            if (submenu.length > 0) {
                submenu = submenu.map(subItem => {
                    const isBlocked = (subItem.tabKey && subItem.subKey && isSubSectionRestricted(subItem.tabKey, subItem.subKey)) ||
                        (subItem.sectionKey && isSectionRestricted(subItem.sectionKey));
                    if (isBlocked) {
                        return {
                            ...subItem,
                            restricted: true,
                            tooltip: `Bloqueado por adeudo: $${Number(userDebt?.adeudo || 0).toFixed(2)}`,
                            icon: <FaLock />,
                        };
                    }
                    return subItem;
                });

                return { ...baseLink, submenu };
            }

            return baseLink;
        };
    }, [isMobile, userData?.rol, modulos.chat_comunitario, isSectionRestricted, isSubSectionRestricted, userDebt]);

    const baseLinks = [
        // Mostrar Comunidad solo si está habilitado o aún está cargando
        ...(loading || modulos.comunidad === true ? [{ to: '/comunidad', label: 'Comunidad', icon: <FaUsers />, tabKey: 'comunidad' }] : []),
        // Mostrar Amenidades solo si está habilitado o aún está cargando
        ...(loading || modulos.amenidades === true ? [{ to: '/amenidades', label: 'Amenidades', icon: <FaSwimmingPool />, tabKey: 'amenidades' }] : []),
        // Mostrar Vigilancia solo si al menos un módulo está habilitado o aún está cargando
        ...(loading || tieneModulosVigilancia ? [{ to: '/vigilancia', label: 'Vigilancia', icon: <FaShieldAlt />, tabKey: 'vigilancia' }] : []),
        // Mostrar Finanzas solo si está habilitado o aún está cargando (excluida de bloqueo total para permitir pagos)
        ...(loading || modulos.finanzas === true ? [{ to: '/finanzas', label: 'Finanzas', icon: <FaFileInvoiceDollar /> }] : []),
        // NOTA: La pestaña de Administración NO se muestra para residentes (rol user)
        // Solo está disponible para superadmin en SuperAdminLayout
        // Mostrar Configuraciones solo si está habilitado o aún está cargando
        ...(loading || modulos.configuraciones === true ? [{ to: '/configuraciones', label: 'Configuraciones', icon: <FaCogs /> }] : []),
    ];

    // Agregar información de restricción a cada link y crear submenús
    const userLinks = baseLinks.map(link => {
        let processedLink = link;

        // Aplicar restricciones
        const isBlocked = (link.tabKey && isTabRestricted(link.tabKey)) ||
            (link.sectionKey && isSectionRestricted(link.sectionKey));

        if (isBlocked) {
            processedLink = {
                ...processedLink,
                restricted: true,
                tooltip: `Bloqueado por adeudo: $${Number(userDebt?.adeudo || 0).toFixed(2)}`,
                icon: <FaLock />,
            };
        }

        // Crear submenús (solo en móvil)
        return createSubmenus(processedLink);
    });

    const toggleSidebar = () => {
        setSidebarVisible(!sidebarVisible);
    };

    // Verificar si estamos en el chat comunitario
    const isChatComunitario = location.pathname === '/comunidad/chat';

    return (
        <ResidentPendingProvider>
            <div className={`user-layout ${isMobile ? (sidebarVisible ? 'sidebar-visible' : 'sidebar-hidden') : 'desktop-mode'}`}>
                {/* Elementos fijos en móvil (solo cuando sidebar cerrada, no en chat comunitario) */}
                {isMobile && !sidebarVisible && !isChatComunitario && (
                    <>
                        {/* Iniciales en esquina superior izquierda */}
                        <div className="user-initials-fixed-wrapper">
                            <div className="user-initials-circle">
                                {getInitials(userData?.nombre || userData?.nombreCompleto || 'Usuario')}
                            </div>
                        </div>

                        {/* Megáfono y Campana en esquina superior derecha */}
                        <div className="fixed-header-actions-wrapper">
                            <ChangelogWidget isStatic={true} />
                            <NotificationBell />
                        </div>
                    </>
                )}

                {/* Header con branding y notificaciones */}
                <header className="user-header">
                    <div className="user-header-content">
                        <div className="user-branding" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            {fraccionamientoConfig.logoUrl ? (
                                <img
                                    src={fraccionamientoConfig.logoUrl}
                                    alt={fraccionamientoConfig.condominioNombre || 'Logo'}
                                    className="user-brand-logo"
                                    style={{ height: '42px', maxWidth: '120px', objectFit: 'contain', borderRadius: '6px', background: '#ffffff', padding: '3px 6px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
                                />
                            ) : (
                                <FaHome className="user-brand-icon" />
                            )}
                            <div className="user-brand-text">
                                <h1>
                                    Portal del Residente
                                    {(fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre) && (
                                        <span className="user-brand-condo-name" style={{ opacity: 0.95, fontWeight: '700', marginLeft: '6px' }}>
                                            — {fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre}
                                        </span>
                                    )}
                                </h1>
                                <p>Gestión de tu Fraccionamiento</p>
                            </div>
                        </div>
                        <div className="user-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <SelectorCasaRapido />
                            <SelectorFraccionamientoRapido />
                            {!isMobile && <NotificationBell />}
                        </div>
                    </div>
                </header>

                {/* Navbar horizontal para desktop */}
                {!isMobile && (
                    <div className="user-navbar-wrapper">
                        <Navbar links={userLinks} isMobile={false} showSubmenus={false} />
                    </div>
                )}

                {/* Sidebar para móvil */}
                {isMobile && (
                    <aside className={`user-sidebar ${sidebarVisible ? 'visible' : 'hidden'}`}>
                        <div className="user-sidebar-header">
                            <div className="user-sidebar-profile-card">
                                <div className="user-sidebar-avatar-container">
                                    <div className="user-sidebar-avatar">
                                        {getInitials(userData?.nombre || userData?.nombreCompleto || 'Usuario')}
                                    </div>
                                    <span className="user-sidebar-status-dot" title="En línea" />
                                </div>
                                <div className="user-sidebar-user-details">
                                    <div className="user-sidebar-name-row">
                                        <span className="user-sidebar-user-name">{userData?.nombre || userData?.nombreCompleto || 'Residente'}</span>
                                        <span className="user-sidebar-role-pill residente">RESIDENTE</span>
                                    </div>
                                    {(fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre) && (
                                        <span className="user-sidebar-condo-badge">
                                            <FaBuilding style={{ fontSize: '0.75rem', flexShrink: 0 }} />
                                            <span className="condo-name-text">{fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                className="user-sidebar-close"
                                onClick={toggleSidebar}
                                aria-label="Cerrar menú"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="user-sidebar-content">
                            <Navbar links={userLinks} isMobile={true} showSubmenus={true} />
                        </div>
                        <div className="user-sidebar-footer">
                            {fraccionamientoConfig.logoUrl ? (
                                <img src={fraccionamientoConfig.logoUrl} alt="Logo" className="user-sidebar-footer-logo" />
                            ) : (
                                <img src="/domus-logo.jpg" alt="Logo" className="user-sidebar-footer-logo" />
                            )}
                            <p className="user-sidebar-footer-name">{fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre || 'Domus Habita'}</p>
                            <p className="user-sidebar-footer-copyright">© 2026 Domus Habita. Todos los derechos reservados</p>
                        </div>
                    </aside>
                )}

                <main className="user-content">
                    <Outlet /> {/* Aquí se renderizará el UserDashboard */}
                </main>

                {/* Botón flotante siempre visible en móvil (solo cuando sidebar está oculto, no en chat comunitario) */}
                {isMobile && !sidebarVisible && !isChatComunitario && (
                    <button
                        className="sidebar-float-btn"
                        onClick={toggleSidebar}
                        aria-label="Mostrar menú"
                        title="Mostrar menú"
                    >
                        <FaBars />
                    </button>
                )}

                {/* Overlay para cerrar el sidebar al hacer clic fuera en móviles */}
                {isMobile && sidebarVisible && (
                    <div
                        className="sidebar-overlay"
                        onClick={toggleSidebar}
                        aria-hidden="true"
                    />
                )}

                {/* Modal de resumen de cargos automáticos */}
                {resumenNoVisto && (
                    <ModalResumenCargosAutomaticos
                        resumen={resumenNoVisto}
                        onClose={() => marcarComoVisto(resumenNoVisto.id)}
                        onMarkAsRead={marcarComoVisto}
                    />
                )}

                {/* Notificaciones Emergentes Críticas (Convenios, Mensajes Admin) */}
                <NotificacionEmergente />

                {/* Asistente Virtual Inteligente (IA) */}
                <DomusAIChatWidget fraccionamientoId={fraccionamientoId} />
            </div>
        </ResidentPendingProvider>
    );
};

export default UserLayout;